import React from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import LoginIcon from '@mui/icons-material/Login'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import {
  useGetFirebaseAnalyticsQuery,
  useGetAppsflyerAnalyticsQuery,
  useGetTotalUsersQuery,
} from 'store/dashboard'
import InfoTooltip from 'components/InfoTooltip'
import { useTimeRange } from './TimeRangeContext'

// ---------------------------------------------------------------------------
// ChangeChip — small pill showing a signed percentage with trend icon
// ---------------------------------------------------------------------------

interface ChangeChipProps {
  value: number
}

const ChangeChip: React.FC<ChangeChipProps> = ({ value }) => {
  const positive = value >= 0
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        bgcolor: positive ? '#E8F5E9' : '#FFEBEE',
        color: positive ? '#2E7D32' : '#C62828',
        borderRadius: '10px',
        px: 0.75,
        py: '2px',
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {positive ? (
        <TrendingUpIcon sx={{ fontSize: 12 }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 12 }} />
      )}
      {positive ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// IconBox — coloured square that houses a white icon
// ---------------------------------------------------------------------------

interface IconBoxProps {
  bgcolor: string
  size: number
  borderRadius?: number
  children: React.ReactNode
}

const IconBox: React.FC<IconBoxProps> = ({
  bgcolor,
  size,
  borderRadius = 1,
  children,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      bgcolor,
      borderRadius,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
)

// ---------------------------------------------------------------------------
// MetricCell — one of the four cells in Zone B
// ---------------------------------------------------------------------------

interface MetricCellProps {
  iconColor: string
  icon: React.ReactNode
  value: number | null | undefined
  label: string
  changePercent?: number | null
  loading?: boolean
  showChange?: boolean
}

const MetricCell: React.FC<MetricCellProps> = ({
  iconColor,
  icon,
  value,
  label,
  changePercent,
  loading = false,
  showChange = true,
}) => (
  <Box
    sx={{
      bgcolor: 'white',
      border: '1px solid #EAECF0',
      borderRadius: 2,
      p: 1.5,
      height: '100%',
    }}
  >
    {/* Line 1: icon (left) + change chip (right) */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <IconBox bgcolor={iconColor} size={32}>
        {icon}
      </IconBox>
      {showChange && !loading && changePercent != null && (
        <ChangeChip value={changePercent} />
      )}
    </Box>

    {/* Line 2: big number */}
    <Box sx={{ mb: 0.25 }}>
      {loading ? (
        <CircularProgress size={16} />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {value != null ? value.toLocaleString() : '—'}
        </Typography>
      )}
    </Box>

    {/* Line 3: label */}
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
)

// ---------------------------------------------------------------------------
// AnalyticsSummaryCards
// ---------------------------------------------------------------------------

const AnalyticsSummaryCards: React.FC = () => {
  const { startDate, endDate } = useTimeRange()

  const { data: firebase, isLoading: fbLoading } =
    useGetFirebaseAnalyticsQuery({ startDate, endDate })

  const { data: appsflyer, isLoading: afLoading } =
    useGetAppsflyerAnalyticsQuery({ startDate, endDate })

  const { data: totalUsers, isLoading: tuLoading } =
    useGetTotalUsersQuery({ startDate, endDate })

  // Treat unconfigured sources as "no data"
  const fbOk = !(firebase && !firebase.isConfigured)
  const afOk = !(appsflyer && !appsflyer.isConfigured)

  // Zone A
  const totalRegisteredUsers = totalUsers?.totalRegisteredUsers ?? null

  // Zone B
  const activeUsers = fbOk ? firebase?.activeUsers : null
  const activeUsersChangePercent = fbOk ? firebase?.activeUsersChangePercent : null

  const installs = afOk ? appsflyer?.installs : null
  const installsChangePercent = afOk ? appsflyer?.installsChangePercent : null
  const uninstalls = afOk ? appsflyer?.uninstalls : null
  const uninstallsChangePercent = afOk ? appsflyer?.uninstallsChangePercent : null

  const signUps = totalUsers?.signUps ?? null
  const signUpsChangePercent = totalUsers?.signUpsChangePercent ?? null

  // Zone C — Conversion Rate = sign-ups / total registered users
  const conversionRate =
    signUps != null && totalRegisteredUsers
      ? `${((signUps / totalRegisteredUsers) * 100).toFixed(1)}%`
      : null

  return (
    <Box>
      {/* ================================================================= */}
      {/* ZONE A — TOP HIGHLIGHT                                             */}
      {/* ================================================================= */}
      <Box sx={{ bgcolor: '#EBF5FF', p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            Total Users
          </Typography>
          <InfoTooltip
            title="Total number of users registered in myHyundai. This metric is not affected by any applied filters."
            iconSize={14}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconBox bgcolor="#002C5F" size={36} borderRadius={2}>
            <PeopleAltIcon sx={{ color: 'white', fontSize: 20 }} />
          </IconBox>
          {tuLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {totalRegisteredUsers != null ? totalRegisteredUsers.toLocaleString() : '—'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ================================================================= */}
      {/* ZONE B — METRICS GRID                                             */}
      {/* ================================================================= */}
      <Box sx={{ bgcolor: '#F8F9FA', p: 1.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <MetricCell
              iconColor="#E3F2FD"
              icon={<DownloadIcon sx={{ color: '#1565C0', fontSize: 18 }} />}
              value={installs}
              label="Install"
              changePercent={installsChangePercent}
              loading={afLoading}
              showChange
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCell
              iconColor="#FFEBEE"
              icon={<DeleteOutlineIcon sx={{ color: '#C62828', fontSize: 18 }} />}
              value={uninstalls}
              label="Uninstall"
              changePercent={uninstallsChangePercent}
              loading={afLoading}
              showChange
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCell
              iconColor="#E8F5E9"
              icon={<PersonAddAltIcon sx={{ color: '#2E7D32', fontSize: 18 }} />}
              value={signUps}
              label="Sign Up"
              changePercent={signUpsChangePercent}
              loading={tuLoading}
              showChange
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCell
              iconColor="#EDE7F6"
              icon={<LoginIcon sx={{ color: '#7B1FA2', fontSize: 18 }} />}
              value={activeUsers}
              label="Sign In"
              changePercent={activeUsersChangePercent}
              loading={fbLoading}
              showChange
            />
          </Grid>
        </Grid>
      </Box>

      {/* ================================================================= */}
      {/* ZONE C — BOTTOM ROW                                               */}
      {/* ================================================================= */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: '#EBF5FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ color: '#1976D2', fontWeight: 500 }}
          >
            Engagement Rate
          </Typography>
          <InfoTooltip
            title='Percentage of total users who signed in and actively used the application.'
            iconSize={14}
            iconColor='#002C5F'
          />
        </Box>

        {tuLoading ? (
          <CircularProgress size={16} />
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {conversionRate ?? '—'}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default AnalyticsSummaryCards
